import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DEFAULT_HEADLINE } from "@/components/ShowcaseView";
import { useSaveShowcaseItems, useSaveShowcaseSettings, useShowcase } from "@/hooks/useShowcase";
import { ORDER_MODES, groupsOf, moveItem, toggleHidden, togglePin } from "@/helpers/showcaseOrder";
import Row from "./ShowcaseManagerRow";
import ShowcaseManagerPreview from "./ShowcaseManagerPreview";
import useShowcaseDrag from "./useShowcaseDrag";
import { HEADLINE_MAX, headlineErrorOf, isShowcaseOn } from "./showcaseManagerModel";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { PICKER_STYLES } from "@/helpers/pickerStyles";
import "./ShowcaseManager.scss";

export { HEADLINE_MAX, headlineErrorOf, isShowcaseOn };

const ShowcaseManager = ({ brokerageId, brokerageName, logoUrl }) => {
  const query = useShowcase(brokerageId);
  const saveItems = useSaveShowcaseItems(brokerageId);
  const saveSettings = useSaveShowcaseSettings(brokerageId);
  const [find, setFind] = useState("");
  const [showHow, setShowHow] = useState(false);
  const [preview, setPreview] = useState(false);
  const [headline, setHeadline] = useState("");
  const [headlineError, setHeadlineError] = useState("");
  const { dragId, overId, handlersFor } = useShowcaseDrag();

  const data = query.data;
  const showcase = data?.showcase;

  useEffect(() => {
    setHeadline(showcase?.headline || "");
    setHeadlineError("");
  }, [brokerageId, showcase?.headline]);

  if (!data) {
    return query.isError ? (
      <div className="showcase-manager__state">
        Your website showcase did not load.{" "}
        <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => query.refetch()}>
          Try again
        </button>
      </div>
    ) : (
      <div className="showcase-manager__state" aria-busy="true">
        Loading your Buyer Want&#8209;Listings…
      </div>
    );
  }

  const items = data.items || [];
  const mode = showcase?.orderMode || "custom";
  const groups = groupsOf(items, mode, find);
  const counts = data.counts || {};
  const on = isShowcaseOn(showcase);

  const fail = (error) => toast.error(error.message);

  const commit = (next, reorder) => {
    if (!next) return;
    saveItems.mutate(next, { onError: fail });
    if (reorder && mode !== "custom") {
      saveSettings.mutate({ orderMode: "custom", headline: showcase?.headline || null }, { onError: fail });
    }
  };

  const onAction = (action, item) => {
    if (action === "up") commit(moveItem(items, item.wantAdId, -1, mode, find), true);
    if (action === "down") commit(moveItem(items, item.wantAdId, 1, mode, find), true);
    if (action === "pin") commit(togglePin(items, item.wantAdId), false);
    if (action === "hide") commit(toggleHidden(items, item.wantAdId), false);
  };

  const { dragPropsOf, groupDrop } = handlersFor({ items, mode, commit });

  const renderRows = (list) =>
    list.map((item, index) => (
      <Row
        key={item.wantAdId}
        item={item}
        first={index === 0}
        last={index === list.length - 1}
        dragging={dragId === item.wantAdId}
        over={overId === item.wantAdId && dragId !== item.wantAdId}
        onAction={onAction}
        dragProps={dragPropsOf(item)}
      />
    ));

  const saveHeadline = () =>
    saveSettings.mutate(
      { orderMode: mode, headline: headline.trim() || null },
      {
        onSuccess: () => toast.success("Your headline is saved."),
        onError: (error) => {
          const field = error.fields?.headline;
          if (field) setHeadlineError(headlineErrorOf(field, error.fields?.headlineTerm));
          else fail(error);
        },
      },
    );

  const totalShown = items.length;
  const filtering = Boolean(find.trim());
  const nothingMatches = filtering && !groups.pinned.length && !groups.rest.length;

  const orderOptions = ORDER_MODES.map((option) => ({ value: option.value, label: option.label }));

  return (
    <div className="showcase-manager">
      <section className="content-block showcase-manager__block">
        <div className="title-sec">
          <h4>Buyer Want&#8209;Listings on your site</h4>
          <a role="button" tabIndex={0} aria-expanded={preview} onClick={() => setPreview(!preview)}>
            {preview ? "Close preview" : "Preview on my site"}
          </a>
        </div>
        <p className="showcase-manager__hint">
          Your agents&apos; Buyer Want&#8209;Listings shared with the public. New ones appear on their own and closed
          ones drop&nbsp;off.
          {on ? "" : " Nothing shows on your website until setup is\u00a0finished."}
        </p>

        {preview ? (
          <ShowcaseManagerPreview
            showcase={showcase}
            brokerageName={brokerageName}
            logoUrl={logoUrl}
            items={items}
            mode={mode}
          />
        ) : null}

        {totalShown ? (
          <>
            <div className="showcase-manager__tools">
              <input
                className="showcase-manager__find"
                type="search"
                value={find}
                onChange={(event) => setFind(event.target.value)}
                placeholder="Find by title, place or agent"
                aria-label="Find a Buyer Want-Listing"
              />
              <div className="showcase-manager__order">
                <SelectDropDown
                  id="showcase-order"
                  className="showcase-manager__order-select"
                  classNamePrefix="select"
                  options={orderOptions}
                  value={orderOptions.find((option) => option.value === mode)}
                  styles={PICKER_STYLES}
                  errorClass="d-none"
                  onChange={(option) =>
                    option &&
                    saveSettings.mutate({ orderMode: option.value, headline: showcase?.headline || null }, { onError: fail })
                  }
                />
              </div>
            </div>
            {groups.pinned.length ? (
              <div className="showcase-manager__group is-pinned" {...groupDrop("pinned")}>
                <ul className="showcase-manager__list">{renderRows(groups.pinned)}</ul>
              </div>
            ) : null}
            <div className="showcase-manager__group" {...groupDrop("rest")}>
              {groups.rest.length ? <ul className="showcase-manager__list">{renderRows(groups.rest)}</ul> : null}
            </div>
            {nothingMatches ? (
              <p className="showcase-manager__hint">
                Nothing matches &ldquo;{find.trim()}&rdquo;. Try a town, an agent or a word from the&nbsp;title.
              </p>
            ) : mode !== "custom" && groups.rest.length > 1 ? (
              <p className="showcase-manager__hint">
                Sorted by {mode === "budget" ? "highest budget" : "newest"}. Moving one switches to your own&nbsp;order.
              </p>
            ) : null}
          </>
        ) : (
          <p className="showcase-manager__hint">
            None yet. Until an agent shares one with the public, your site shows &ldquo;Tell us what you&apos;re looking
            for&rdquo;, which sends visitors to a buyer form for your&nbsp;agents.
          </p>
        )}

        <p className="showcase-manager__counts">
          {counts.public ?? totalShown} shared with the public
          {counts.membersOnly ? `, ${counts.membersOnly} more with members only` : ""}.{" "}
          <button type="button" className="showcase-manager__link" aria-expanded={showHow} onClick={() => setShowHow(!showHow)}>
            How agents share with the public
          </button>
        </p>
        {showHow ? (
          <ol className="showcase-manager__how">
            <li>The agent opens their Buyer Want&#8209;Listing and taps&nbsp;Edit.</li>
            <li>Under Want&#8209;Listing Syndication they turn on &ldquo;Syndicate to the public via Gitcha.com&rdquo;.</li>
            <li>They save. It shows here and on your site within a&nbsp;minute.</li>
          </ol>
        ) : null}
      </section>

      <section className="content-block showcase-manager__block">
        <div className="title-sec">
          <h4>Headline on your site</h4>
        </div>
        <div className="showcase-manager__headline">
          <input
            value={headline}
            maxLength={HEADLINE_MAX}
            onChange={(event) => {
              setHeadline(event.target.value);
              setHeadlineError("");
            }}
            placeholder={DEFAULT_HEADLINE.replace(" ", " ")}
            aria-label="Headline on your site"
            aria-invalid={Boolean(headlineError)}
          />
          <button
            type="button"
            className="showcase-manager__link"
            disabled={(headline.trim() || null) === (showcase?.headline || null) || saveSettings.isPending}
            onClick={saveHeadline}
          >
            Save
          </button>
        </div>
        {headlineError ? (
          <p className="showcase-manager__error" role="alert">
            {headlineError}
          </p>
        ) : (
          <p className="showcase-manager__hint">Leave it empty to use &ldquo;{DEFAULT_HEADLINE.replace(" ", " ")}&rdquo;.</p>
        )}
      </section>
    </div>
  );
};

export default ShowcaseManager;
